import { forwardRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { colors } from "brand";
import { RideStatusBadge } from "common-fe";
import { useTranslation } from "i18n-fe";
import { RIDE_STATUSES } from "score-common";
import { RIDE_ROLE, RESULT_STATUS } from "../utils/orderOfGo";
import { formatTime } from "../utils/formatTime";
import { formatOrdinal } from "../utils/formatOrdinal";
import { formatComposite } from "../utils/formatComposite";

// #642 — ONE ride in the running order.
// #643 — expands (accordion, collapsed by default) to the full announcer
// script: class/test/qualifying, horse detail, pedigree, owner/breeder, and
// the rider's home town — the SAME fields, in the SAME order, as the printed
// `announcer-order-of-go` report's per-ride block (docs/score/announcer.md),
// so the announcer's eye already knows where to look. Every field in that
// script comes off `row` (joined in ../utils/orderOfGo.js buildRideRow) — no
// fetch here, and all of it is #525 ingest-carry, so ALL of it is optional:
// a horse with no sire is normal, never an error, and a missing field never
// renders as a bare label over nothing.
//
// Read at arm's length, out loud, under time pressure: big type, high contrast,
// minimal chrome (docs/score/announcer.md, "Screens").
//
// ⚠️ The brand theme's `primary.main` is dark grey and vanishes on a dark
// background — every emphasis colour here is `colors.foxOrange` or `text.*`.

const ROLE_STYLES = {
  [RIDE_ROLE.CURRENT]: {
    testId: "role-current",
    sx: { bgcolor: colors.foxOrange, color: "#000", fontWeight: 800 },
  },
  [RIDE_ROLE.ON_DECK]: {
    testId: "role-on-deck",
    sx: {
      bgcolor: "transparent",
      color: colors.foxOrange,
      border: `2px solid ${colors.foxOrange}`,
      fontWeight: 700,
    },
  },
  [RIDE_ROLE.NEXT]: {
    testId: "role-next",
    sx: {
      bgcolor: "transparent",
      color: "text.secondary",
      border: "2px solid",
      borderColor: "divider",
      fontWeight: 700,
    },
  },
};

// Static literal keys only — never t(`oog.${role}`).
function roleLabel(role, t) {
  if (role === RIDE_ROLE.CURRENT) return t("oog.inTheRing");
  if (role === RIDE_ROLE.ON_DECK) return t("oog.onDeck");
  if (role === RIDE_ROLE.NEXT) return t("oog.next");
  return "";
}

// Judges are labelled by their single-letter ring position (C/E/H/M/B, from the
// class judgeAssignments — same convention as scribe-fe's JudgePositionBadge),
// falling back to the opaque judgeId only when score-api couldn't resolve one.
function judgeDisplay(entry) {
  return entry.position || entry.judgeId || "?";
}

// One label -> value line. Renders nothing when the value is empty, so a
// missing #525 field never leaves a bare label behind (AC#2).
function DetailLine({ label, value, testId }) {
  if (!value) return null;
  return (
    <Box data-testid={testId}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", lineHeight: 1.6, letterSpacing: 1 }}
      >
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={500} sx={{ color: "text.primary" }}>
        {value}
      </Typography>
    </Box>
  );
}

const RideRow = forwardRef(function RideRow({ row, result }, ref) {
  const { t } = useTranslation("announcer");
  const [expanded, setExpanded] = useState(false);
  const isScratched = row.status === RIDE_STATUSES.SCRATCHED;
  const isCurrent = row.role === RIDE_ROLE.CURRENT;
  const roleStyle = row.role ? ROLE_STYLES[row.role] : null;

  // #644/#699 — this ride's result state drives the right-hand badge. score-api
  // (#697) reports PARTIAL | CONFIRMED | FINAL and WITHHOLDS every number while
  // PARTIAL (composite null, no per-judge %), so one judge's mark can never
  // render as if it were the score. Reveal is driven ONLY by result.status
  // (never row.status/cache), so an offline CONFIRMED ride is never mistaken for
  // a signed FINAL one (#699):
  //   PARTIAL   → per-judge "committed" chips, NO scores, NO composite.
  //   CONFIRMED → composite + each judge's %, green "ready to announce" (visually
  //               distinct from FINAL's orange placing badge).
  //   FINAL     → unchanged: the orange placing/composite badge.
  const resultStatus = result?.status ?? null;
  const isResultFinal = resultStatus === RESULT_STATUS.FINAL;
  const isResultConfirmed = resultStatus === RESULT_STATUS.CONFIRMED;
  const isResultPartial = resultStatus === RESULT_STATUS.PARTIAL;
  const perJudge = result?.perJudge ?? [];
  const expectedJudgeCount = result?.expectedJudgeCount ?? 0;
  const confirmedCount = result?.confirmedCount ?? perJudge.length;
  const placingLabel = isResultFinal
    ? result.placing
      ? t("results.placingBadge", {
          place: formatOrdinal(result.placing),
          composite: formatComposite(result.composite),
        })
      : t("results.unplacedBadge", { composite: formatComposite(result.composite) })
    : "";

  // A scratch is a status, not a deletion — the row STAYS, struck through and
  // dimmed, so the announcer knows not to expect that horse. Same treatment as
  // scribe-fe's RideList (#460).
  const struck = isScratched ? { textDecoration: "line-through" } : null;

  // Same field order as announcer-order-of-go.template.js's per-ride block:
  // class/test/qualifying, horse detail, pedigree, owner+breeder, address.
  const classParts = [row.classNumber, row.className].filter(Boolean).join(" ");
  const classLine = [classParts, row.testName].filter(Boolean).join(" · ");
  const hasClassDetail = Boolean(classLine || row.qualifying || row.termReason);
  const hasAnyDetail =
    hasClassDetail || row.horseDetail || row.pedigree || row.ownerLine || row.address;

  return (
    <Accordion
      ref={ref}
      expanded={expanded}
      onChange={(_event, next) => setExpanded(next)}
      disableGutters
      data-testid="ride-row"
      data-ride-id={row.id}
      data-role={row.role || undefined}
      data-scratched={isScratched || undefined}
      aria-disabled={isScratched || undefined}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        opacity: isScratched ? 0.55 : 1,
        border: "solid",
        // The current ride is PINNED visually — it must be findable at a glance
        // from across a ring stand, in either theme.
        borderWidth: isCurrent ? 3 : 1,
        borderColor: isCurrent ? colors.foxOrange : "divider",
        boxShadow: isCurrent ? 6 : 0,
        // A scratched ride carries no action — no expand, no hover affordance.
        pointerEvents: isScratched ? "none" : "auto",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon data-testid="ride-row-expand-icon" sx={{ color: "text.secondary" }} />
        }
        aria-label={expanded ? t("detail.collapse") : t("detail.expand")}
        data-testid="ride-row-summary"
        // Strike at every level of the summary (root, MUI's interposed content
        // <div>, and the leaf Typographies): a scratch has to read as struck no
        // matter which node you look at.
        sx={{
          px: 2,
          py: 1,
          ...struck,
          "& .MuiAccordionSummary-content": { ...struck },
        }}
      >
        {/* The strike also goes on the wrapper, not just the leaf Typographies:
            AccordionSummary interposes its own content <div>, and a scratch has
            to read as struck no matter which node you look at. */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          sx={{ flex: 1, ...struck }}
        >
          <Box sx={{ minWidth: 96 }}>
            <Typography variant="h5" fontWeight={700} sx={{ ...struck }}>
              {formatTime(row.scheduledTime)}
            </Typography>
          </Box>

          <Chip
            label={row.competitorNumber || "—"}
            data-testid="ride-cno"
            sx={{
              fontSize: "1.15rem",
              fontWeight: 800,
              height: 40,
              minWidth: 64,
              color: "text.primary",
              borderColor: "divider",
              ...struck,
            }}
            variant="outlined"
          />

          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="h5" fontWeight={700} sx={{ ...struck }}>
              {row.riderName || "—"}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ ...struck }}>
              {row.horseName || "—"}
            </Typography>
          </Box>

          <Stack spacing={0.5} alignItems="flex-end">
            {placingLabel ? (
              // #925 (bug #80/#81 rollup) — a FINAL ride with no placing (cut,
              // eliminated, etc.) reads as amber, not the orange "placed" look
              // above it or a grey/red one — same Chip, same slot, only the
              // color/fill changes (no reposition).
              <Chip
                label={placingLabel}
                data-testid="ride-placing-badge"
                data-placing={result.placing ?? undefined}
                color={result.placing ? undefined : "warning"}
                variant={result.placing ? "outlined" : "filled"}
                sx={{
                  bgcolor: result.placing ? "transparent" : undefined,
                  color: result.placing ? colors.foxOrange : undefined,
                  border: result.placing ? `2px solid ${colors.foxOrange}` : undefined,
                  fontWeight: 800,
                  fontSize: "1.05rem",
                  height: 36,
                  ...struck,
                }}
              />
            ) : null}
            {/* #699 CONFIRMED — every judge confirmed, not yet signed. Reveal the
                composite + each judge's %, in green ("ready to announce"), which
                MUST read differently from FINAL's orange placing badge above. The
                success colour matches common-fe RideStatusBadge's CONFIRMED/FINAL
                chip, and is legible in both themes (MUI semantic success).
                #925 (bug #80/#81) — this is the green "ready to announce" tag
                Evin flagged as having stopped popping: same Chip, same slot,
                just bumped to the same large/bold treatment as the per-judge
                scores below so it reads at a glance again. Each judge chip now
                stacks in its OWN column row (one judge per line) instead of a
                wrapping row, and both read "Judge at <letter>: XX.XXX%" large
                enough to read across a ring stand — no element moved. */}
            {isResultConfirmed ? (
              <Stack
                spacing={0.5}
                alignItems="flex-end"
                data-testid="ride-confirmed"
                data-composite={result.composite ?? undefined}
              >
                <Chip
                  color="success"
                  icon={<CheckCircleIcon />}
                  label={t("results.confirmedBadge", {
                    composite: formatComposite(result.composite),
                  })}
                  data-testid="ride-confirmed-badge"
                  sx={{ fontWeight: 800, fontSize: "1.15rem", height: 40, px: 0.5 }}
                />
                <Stack spacing={0.5} alignItems="flex-end">
                  {perJudge.map((j) => (
                    <Chip
                      key={judgeDisplay(j)}
                      variant="outlined"
                      color="success"
                      data-testid="judge-score-chip"
                      data-position={j.position || undefined}
                      label={t("results.judgeScore", {
                        position: judgeDisplay(j),
                        value: formatComposite(j.percentage),
                      })}
                      sx={{ fontWeight: 700, fontSize: "1.05rem", height: 36, px: 0.5 }}
                    />
                  ))}
                </Stack>
              </Stack>
            ) : null}
            {/* #699 PARTIAL — some judges have committed their marks, others
                haven't. Show WHICH judges are committed, and NEVER a score or a
                (half-)composite: score-api withholds every number while PARTIAL. */}
            {isResultPartial ? (
              <Stack
                spacing={0.5}
                alignItems="flex-end"
                data-testid="ride-partial-committed"
                data-confirmed-count={confirmedCount}
                data-expected-count={expectedJudgeCount || undefined}
              >
                <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="flex-end">
                  {perJudge.map((j) => (
                    <Chip
                      key={judgeDisplay(j)}
                      size="small"
                      color="success"
                      icon={j.marksConfirmed ? <CheckCircleIcon /> : undefined}
                      data-testid="judge-committed-chip"
                      data-position={j.position || undefined}
                      data-confirmed={j.marksConfirmed ? "true" : "false"}
                      label={t("results.judgeCommitted", { position: judgeDisplay(j) })}
                      sx={{ fontWeight: 700 }}
                    />
                  ))}
                </Stack>
                {expectedJudgeCount > 0 ? (
                  <Typography variant="body2" color="text.secondary" data-testid="ride-committed-count">
                    {t("results.judgeCommittedCount", {
                      confirmed: confirmedCount,
                      expected: expectedJudgeCount,
                    })}
                  </Typography>
                ) : null}
              </Stack>
            ) : null}
            {roleStyle ? (
              <Chip
                label={roleLabel(row.role, t)}
                data-testid={roleStyle.testId}
                sx={{ ...roleStyle.sx, fontSize: "1rem", height: 34, px: 0.5 }}
              />
            ) : null}
            {/* #758 unified the WORDING ("In the ring" both places) but left TWO
                chips saying it — the role chip above plus this status badge —
                stacked on the current ride (bug #80). #925: when the role chip
                ALREADY says "In the ring" (isCurrent + IN_PROGRESS), drop this
                one entirely rather than render the same phrase twice; every
                other status (Scheduled/Confirmed/Scored/Final/Scratched) still
                gets its own badge exactly as before. */}
            {isCurrent && row.status === RIDE_STATUSES.IN_PROGRESS ? null : (
              <RideStatusBadge
                status={row.status}
                labels={{
                  [RIDE_STATUSES.SCRATCHED]: t("oog.scratched"),
                  [RIDE_STATUSES.IN_PROGRESS]: t("oog.inTheRing"),
                }}
                sx={{ fontWeight: 700, ...struck }}
              />
            )}
            {row.testName ? (
              <Typography variant="body2" color="text.secondary" sx={{ ...struck }}>
                {row.testName}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails data-testid="ride-row-detail" sx={{ px: 2, pb: 2, pt: 0 }}>
        {hasAnyDetail ? (
          <Stack spacing={1.75} sx={{ borderTop: "1px solid", borderColor: "divider", pt: 1.75 }}>
            {hasClassDetail ? (
              <Box data-testid="detail-class">
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.6, letterSpacing: 1 }}
                >
                  {t("detail.class")}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="h6" fontWeight={500} sx={{ color: "text.primary" }}>
                    {classLine || "—"}
                  </Typography>
                  {row.qualifying ? (
                    <Chip
                      size="small"
                      label={t("detail.qualifying")}
                      data-testid="detail-qualifying"
                      sx={{ bgcolor: colors.foxOrange, color: "#000", fontWeight: 700 }}
                    />
                  ) : null}
                  {row.termReason ? (
                    <Chip size="small" variant="outlined" label={row.termReason} />
                  ) : null}
                </Stack>
              </Box>
            ) : null}

            <DetailLine testId="detail-horse" label={t("detail.horse")} value={row.horseDetail} />
            <DetailLine testId="detail-pedigree" label={t("detail.pedigree")} value={row.pedigree} />
            <DetailLine
              testId="detail-connections"
              label={t("detail.connections")}
              value={row.ownerLine}
            />
            <DetailLine testId="detail-rider-from" label={t("detail.riderFrom")} value={row.address} />
          </Stack>
        ) : (
          <Typography
            data-testid="detail-empty"
            color="text.secondary"
            sx={{ borderTop: "1px solid", borderColor: "divider", pt: 1.75 }}
          >
            {t("detail.empty")}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
});

export default RideRow;
